import { CommonModule } from '@angular/common';
import { Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { HabitStore } from '@app/_stores';
import { HabitTimerService } from '@app/_services/habit-timer.service';
import { SettingsService } from '@app/_services/settings.service';
import { ParseSecondsPipe } from '@app/_pipes/parse-seconds.pipe';
import { HabitTimerState } from '@app/_interfaces/habit.interface';
import { MatDialog } from '@angular/material/dialog';
import { HabitFormComponent } from '@app/components/features/habits/habit-form/habit-form.component';
import { ModalService } from '@app/_services/modal.service';
import { TimeFormatPipe } from '@app/_pipes/time-format.pipe';

@Component({
  selector: 'app-habit-timer-tracker',
  imports: [
    CommonModule,
    TimeFormatPipe
  ],
  templateUrl: './habit-timer-tracker.component.html',
  styleUrl: './habit-timer-tracker.component.scss'
})
export class HabitTimerTrackerComponent {

  readonly habitStore = inject(HabitStore);
  readonly timerService = inject(HabitTimerService);
  readonly ss = inject(SettingsService);
  private dialog = inject(MatDialog);
  readonly modalService = inject(ModalService);

  isExpanded: WritableSignal<boolean> = signal(false);

  timers: Signal<HabitTimerState[]> = computed(() =>
    this.timerService.addedTimers().filter(timer =>
      this.habitStore.idToSetup()[timer.setupId]?.deletedAt === null
    )
  );

  getTotalSeconds(timer: HabitTimerState): number {
    const setup = this.habitStore.idToSetup()[timer.setupId];
    return timer.totalSeconds * setup.roundCount;
  }

  getTimerProgressPercentage(timer: HabitTimerState): number {
    if (!timer.totalSeconds) return 0;
    const totalGoalSeconds = this.getTotalSeconds(timer);
    if (totalGoalSeconds === 0) return 0;
    return Math.min((timer.totalSecondsSpent / totalGoalSeconds) * 100, 100);
  }

  getTimerProgressBg(timer: HabitTimerState): string {
    const percentage = this.getTimerProgressPercentage(timer);
    return `conic-gradient(${this.ss.theme().color12} ${percentage * 3.6}deg, ${this.ss.theme().color38} 0deg)`;
  }

  toggleTimers() {
    this.isExpanded.set(!this.isExpanded());
  }

  openHabit(timer: HabitTimerState) {
    const habit = this.habitStore.habitMap().setups[timer.setupId]?.[timer.dateString] || null;
    const confirmDialog = this.modalService.openLevel1(
      HabitFormComponent,
      {
        mode: habit ? 'edit' : 'new',
        value: habit,
        setupId: timer.setupId,
        dateString: timer.dateString
      },
      { disableClose: true, autoFocus: false }
    );

    // Close the expanded drawer when opening a habit
    if (this.isExpanded()) {
      this.isExpanded.set(false);
    }
  }
}
